import type { CandidateStatusParam } from '@/hooks/useRouterQuery';

/**
 * Single source of truth for translating raw `cadence_template_candidate.status`
 * values into human-readable labels. Used by:
 *   - the staging-table status pill (`STATUS_LABELS` in `CampaignTemplateStaging.tsx`)
 *   - the candidate-status filter dropdown options (`Filters2/Filters.tsx`)
 *   - any future status badge / surface
 *
 * Falls back to the raw string when an unknown status sneaks in (defensive —
 * means we don't ship a broken UI when the worker adds a new status before
 * the client redeploys).
 */
const LABELS: Record<CandidateStatusParam, string> = {
  pending: 'Pending',
  researching: 'Researching',
  research_failed: 'Failed',
  gathering_sources: 'Searching the web',
  synthesizing: 'Synthesizing dossier',
  synthesize_failed: 'Dossier failed',
  filter_failed: 'Filtered out',
  personalizing: 'Drafting',
  personalize_failed: 'Drafting failed',
  rate_limited: 'Rate limited',
  account_disconnected: 'LinkedIn disconnected',
  awaiting_capacity: 'Waiting for capacity',
  retrying: 'Retrying',
  pending_review: 'Needs review',
  rejected: 'Rejected',
  promoted: 'Enrolled',
  removed: 'Removed',
};

export function humanizeCandidateStatus(status: string | null | undefined): string {
  if (status == null || status === '') return '';
  // Cast is safe: unknown values fall through to the raw string below.
  const known = LABELS[status as CandidateStatusParam];
  return known ?? status;
}

/** Convenience for code paths that already know the status is well-typed. */
export const candidateStatusLabel = (status: CandidateStatusParam): string => LABELS[status];

/**
 * Candidate status → Button color (rendered with `tone="soft"`). Single source
 * of truth shared by the staging-table status pill (`CampaignTemplateStaging`)
 * and the candidate → enrollment flow graph (`CandidateFlowGraph`), so the color
 * semantics stay in lockstep with the labels above: red = bad/failed, green = enrolled, blue =
 * in-flight, yellow = needs attention. `undefined` renders as the default gray.
 */
export const candidateStatusColor: Record<CandidateStatusParam, 'red' | 'green' | 'blue' | 'yellow' | undefined> = {
  pending: undefined,
  researching: 'blue',
  research_failed: 'red',
  gathering_sources: 'blue',
  synthesizing: 'blue',
  synthesize_failed: 'red',
  filter_failed: 'yellow',
  personalizing: 'blue',
  personalize_failed: 'red',
  // Blocked (alive, auto-resumes) → yellow "needs attention", except the
  // internal capacity/retry waits which are routine in-flight → blue.
  rate_limited: 'yellow',
  account_disconnected: 'red',
  awaiting_capacity: 'blue',
  retrying: 'blue',
  pending_review: 'yellow',
  rejected: 'red',
  promoted: 'green',
  removed: undefined,
};

/**
 * Fallback short label for the reason a Campaign Switch candidate was errored
 * out, used only when the worker-stamped `failure_reason` is absent (legacy
 * rows). A switch-errored candidate is `status = 'filter_failed'` disambiguated
 * by `failure_type`:
 *   - `switch_no_match` → the contact's attribute matched no configured range
 *   - `switch_no_data`  → the routing attribute couldn't be resolved
 *
 * These labels are intentionally attribute-AGNOSTIC: a switch can route on age,
 * ~AUM, or any future attribute, so we must not hard-code an attribute name
 * here. When available, prefer the backend's `failure_reason` (which names the
 * actual attribute, e.g. "No ~AUM data available for this contact") — see
 * `RoutedCampaignBadge`.
 */
export function humanizeSwitchOutcome(failureType: string | null | undefined): string {
  if (failureType === 'switch_no_match') return 'No matching range';
  if (failureType === 'switch_no_data') return 'Missing attribute data';
  return 'Filtered out';
}

/**
 * Short label for WHY a candidate ended up `status = 'rejected'`, disambiguated by
 * `failure_type`. Kept concise for the "Routed to" badge — the full worker-stamped
 * `failure_reason` (e.g. `Contact already has an active campaign: "Seniors"`) is
 * shown on hover, so this is just the at-a-glance summary.
 */
export function humanizeRejection(failureType: string | null | undefined): string {
  switch (failureType) {
    case 'already_in_cadence':
      return 'Already in a campaign';
    case 'icp_disqualified':
    case 'title_screen_disqualified':
      return 'Not a fit';
    case 'user_rejected':
      return 'Removed by you';
    default:
      return 'Rejected';
  }
}
